import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { node, string } from 'prop-types'

import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { H4, fontWeights, fontSizes } from 'styles/typography'
import { borderColor, white, greyLight, black } from 'styles/colors'
import { borderRadius } from 'styles/global'

import Icon from 'components/Icon'
import DownChevronLarge from 'icons/down-chevron-large.svg'

const Container = styled.div`
  position: relative;
  border-radius: ${borderRadius};
  background-color: ${white};
  box-shadow: 0px 2px 21px rgba(0, 0, 0, 0.08);
`

const Trigger = styled.button`
  display: flex;
  align-items: center;
  padding: ${spacing.large};
  width: 100%;
  text-align: left;

  ${media.small`
    padding: ${spacing.xLarge};
  `};

  ${media.medium`
    padding: ${spacing.xLarge} ${spacing.xxLarge};
  `};

  ${media.large`
    padding: ${spacing.xxxLarge};
  `};
`

const TriggerContent = styled.div`
  flex: 1;
  padding-right: ${spacing.large};

  ${media.medium`
    display: flex;
    padding-right: ${spacing.xLarge};
  `};
`

const Heading = styled(H4)`
  text-transform: uppercase;
  font-weight: ${fontWeights.regular};
  letter-spacing: 0.1em;
  margin-bottom: ${spacing.small};

  ${media.medium`
    margin-bottom: 0;
    margin-right: ${spacing.xLarge};
  `};

  ${media.large`
    margin-right: ${spacing.xxLarge};
    min-width: 300px;
  `};

  ${media.xLarge`
    margin-right: 56px;
  `};
`

const Subheading = styled.p`
  margin-bottom: 0;
`

const IconWrap = styled.div`
  display: flex;
  color: ${greyLight};

  ${props =>
    props.flipped &&
    css`
      transform: rotate(180deg);
    `}
`

const Content = styled.div`
  padding: ${spacing.large};
  padding-top: 0;
  text-align: center;

  ${media.small`
    padding: ${spacing.xLarge};
    padding-top: 0;
  `};

  ${media.medium`
    padding: ${spacing.xLarge} ${spacing.xxLarge};
    padding-top: 0;
  `};

  ${media.large`
    padding: ${spacing.xxLarge} ${spacing.xxxLarge};
    padding-top: 0;
  `};
`

const Inner = styled.div`
  display: grid;
  grid-gap: ${spacing.large};
  border-top: 1px solid ${borderColor};
  padding-top: ${spacing.large};

  ${media.small`
    grid-template-columns: repeat(2, 1fr);
    padding-top: ${spacing.xLarge};
  `};

  ${media.medium`
    grid-template-columns: repeat(4, 1fr);
    grid-gap: ${spacing.xLarge};
  `};

  ${media.large`
    grid-gap: ${spacing.xxLarge};
    padding: 0 ${spacing.xLarge};
    padding-top: ${spacing.xxxLarge};
  `};

  ${media.xLarge`
    grid-gap: 56px;
    padding: 0 ${spacing.xxLarge};
    padding-top: ${spacing.xxxLarge};
  `};
`

const FeatureContainer = styled.div`
  text-align: left;

  ${media.medium`
    text-align: center;
  `};

  p {
    font-size: ${fontSizes.small};
  }
`

const FeatureHeading = styled.span`
  font-weight: ${fontWeights.medium};
  display: block;
  color: ${black};
  text-transform: uppercase;
  margin-bottom: ${spacing.tiny};

  ${media.medium`
    margin-bottom: ${spacing.small};
  `};
`

const SubscriptionInfoBox = () => {
  const [expanded, setExpanded] = useState(false)
  return (
    <Container>
      <Trigger
        onClick={() => {
          setExpanded(!expanded)
        }}
      >
        <TriggerContent>
          <Heading>Subscribe & Save</Heading>
          <Subheading>
            Get up to <strong>30% off</strong> repeat pod shipments
          </Subheading>
        </TriggerContent>
        <IconWrap flipped={expanded}>
          <Icon icon={<DownChevronLarge />} />
        </IconWrap>
      </Trigger>
      {expanded && (
        <Content>
          <Inner>
            <Feature
              heading="Save 20%"
              content={<p>With a purchase of 3-4 packs of any flavour at checkout. Includes free shipping.</p>}
            />
            <Feature
              heading="Save 25%"
              content={<p>With a purchase of 5 packs or more of any flavour at checkout. Includes free shipping.</p>}
            />
            <Feature
              heading="Save additional 5%"
              content={
                <p>
                  Only available for <strong>Vuse+ Platinum</strong> members.
                </p>
              }
            />
            <Feature
              heading="Free device on 1st purchase"
              content={<p>Only available for Vuse+ Platinum members on Subscription purchase.</p>}
            />
          </Inner>
        </Content>
      )}
    </Container>
  )
}

const Feature = ({ heading, content }) => (
  <FeatureContainer>
    <FeatureHeading>{heading}</FeatureHeading>
    {content}
  </FeatureContainer>
)

Feature.propTypes = {
  content: node.isRequired,
  icon: string.isRequired
}

export default SubscriptionInfoBox
